import { defineStore } from 'pinia'
import { runClear } from '@/dev/seed/runClear'
import { runSeed } from '@/dev/seed/runSeed'
import { runSeedOrders } from '@/dev/seed/runSeedOrders'
import {
  runSeedCustomerPricing,
  type ISeedCustomerPricingResult,
} from '@/dev/seed/runSeedCustomerPricing'
import { runClearCustomerPricing } from '@/dev/seed/runClearCustomerPricing'
import { EMU } from '@/globals/typescript/utils/host'
import { useAccountStore } from '@/stores/accountStore'
import { useAuthStore } from '@/stores/authStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useOrderStore } from '@/stores/orderStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'

export const useDevSeedStore = defineStore('devSeed', {
  state: () => ({
    isSeeding: false,
    isSeedingOrders: false,
    isClearing: false,
    isClearingOrders: false,
    isSeedingCustomerPricing: false,
    isClearingCustomerPricing: false,
  }),

  getters: {
    /**
     * "Empty" for seeding purposes: no sample areas. In the Firestore emulator only, a catalogue that
     * holds nothing but the Stage baseline's sample areas (IDs start with `aitest-`, see
     * lab-cloud/stage-test-lab) counts as empty too, so the emulator's `ai-test` lab can be seeded for
     * the old e2e suite. NB: the seed re-points EVERY customer of the lab at its own pricing segment
     * and rewrites the drop points' walk-in TATs, so on the baseline lab it overwrites the baseline's
     * pricing scenarios (the narrow partner segment). That is fine in a disposable emulator and wrong
     * anywhere real — hence emulator only: on real Dev the strict rule stands. Once any seeded sample
     * area exists this is false again, so the catalogue cannot be seeded twice.
     */
    isCatalogEmpty: (): boolean => {
      const sampleAreas = useSampleAreaStore().allSampleAreas
      if (sampleAreas.length === 0) return true
      return (
        !!EMU.isEmulatingFirestore &&
        sampleAreas.every((x) => x.id.startsWith('aitest-'))
      )
    },
    hasOrders: (): boolean =>
      useOrderStore().allOrders.length > 0,
    canSeedOrders: (): boolean => {
      return (
        useSampleAreaStore().allSampleAreas.length > 0 &&
        useCustomerAccountStore().allCustomerAccounts.length > 0 &&
        // Business users only — the business blueprints never take an insurance or private user (MIL-402).
        useCustomerUserStore().customerUsersBusiness.length > 0
      )
    },
    /**
     * Customer pricing overrides inherit from a segment, so there must be a
     * priced segment to inherit from and at least one customer of any type to
     * write onto. Mirrors how canSeedOrders gates on its own prerequisites.
     */
    canSeedCustomerPricing: (): boolean => {
      const hasPricedSegment = usePricingSegmentStore().allPricingSegments.some(
        (x) =>
          x.activeState === 'active' &&
          x.analyses.length > 0 &&
          x.tats.length > 0
      )
      const hasAnyCustomer =
        useCustomerAccountStore().allCustomerAccounts.length > 0 ||
        useInsuranceCustomerAccountStore().allInsuranceCustomerAccounts.length > 0 ||
        useCustomerUserStore().customerUsersPrivate.length > 0
      return hasPricedSegment && hasAnyCustomer
    },
    isAnyOperationInProgress: (state): boolean =>
      state.isSeeding ||
      state.isSeedingOrders ||
      state.isClearing ||
      state.isClearingOrders ||
      state.isSeedingCustomerPricing ||
      state.isClearingCustomerPricing,
  },

  actions: {
    async seedCatalog(): Promise<void> {
      const accountID = useAccountStore().labAccount?.id
      if (!accountID) throw new Error('[devSeedStore] No lab account loaded')

      const userID = useAuthStore().user?.uid ?? ''

      this.isSeeding = true
      try {
        await runSeed(accountID, userID)
      } finally {
        this.isSeeding = false
      }
    },

    async seedOrders(
      customerAccountID: string,
      customerUserID: string
    ): Promise<void> {
      const accountID = useAccountStore().labAccount?.id
      if (!accountID) throw new Error('[devSeedStore] No lab account loaded')

      const userID = useAuthStore().user?.uid ?? ''

      this.isSeedingOrders = true
      try {
        await runSeedOrders(accountID, userID, customerAccountID, customerUserID)
      } finally {
        this.isSeedingOrders = false
      }
    },

    async seedCustomerPricing(): Promise<ISeedCustomerPricingResult> {
      const accountID = useAccountStore().labAccount?.id
      if (!accountID) throw new Error('[devSeedStore] No lab account loaded')

      const userID = useAuthStore().user?.uid ?? ''

      this.isSeedingCustomerPricing = true
      try {
        return await runSeedCustomerPricing(accountID, userID)
      } finally {
        this.isSeedingCustomerPricing = false
      }
    },

    async clearCustomerPricing(): Promise<number> {
      const accountID = useAccountStore().labAccount?.id
      if (!accountID) throw new Error('[devSeedStore] No lab account loaded')

      this.isClearingCustomerPricing = true
      try {
        return await runClearCustomerPricing(accountID)
      } finally {
        this.isClearingCustomerPricing = false
      }
    },

    async clearOrders(): Promise<void> {
      this.isClearingOrders = true
      try {
        await useOrderStore().deleteAllOrdersAndSamples()
      } finally {
        this.isClearingOrders = false
      }
    },

    async clearAll(): Promise<void> {
      const accountID = useAccountStore().labAccount?.id
      if (!accountID) throw new Error('[devSeedStore] No lab account loaded')

      this.isClearing = true
      try {
        await runClear(accountID)
      } finally {
        this.isClearing = false
      }
    },
  },
})
